import React, { useEffect } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import { addPageNumber } from '../common/addPageNumber';

interface Props {
    data: any;
    setPdfBlob: (blob: Blob) => void;
    formData?: any;
}

// Times arrive as "HH:mm" from the API; the rest of the reports show 12 hour.
const to12Hour = (time: string | null | undefined): string => {
    if (!time || time === '-') return '';
    const [rawHours, minutes] = time.split(':');
    let hours = parseInt(rawHours, 10);
    if (isNaN(hours)) return '';
    const suffix = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${suffix}`;
};

// Additional Attendance: everyone who worked a site they were not scheduled
// for.
//
// This is the list an admin checks a timesheet against, so the column that
// matters most is the site they were originally due at. A row still without a
// paycode is on no timesheet at all, and is the one to act on - it is set in
// bold rather than given a column of its own.
const UnscheduledAttendanceReportPdf: React.FC<Props> = ({ data, setPdfBlob, formData }) => {
    const generatePDF = () => {
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

        doc.setFontSize(14);
        doc.text('Additional Attendance', 14, 15);

        // The range asked for, so a printed copy says what it covers.
        const from = formData?.StartDate ? moment(formData.StartDate).format('MM/DD/YYYY') : null;
        const to = formData?.EndDate ? moment(formData.EndDate).format('MM/DD/YYYY') : null;
        doc.setFontSize(9);
        doc.text(
            from || to ? `Dates worked: ${from ?? 'any'} to ${to ?? 'any'}` : 'All dates',
            14,
            21
        );

        const rows: any[] = Array.isArray(data) ? data : [];

        const uncoded = rows.filter((r: any) => r?.Coded === 'No').length;
        if (uncoded > 0) {
            doc.text(
                `${uncoded} ${uncoded === 1 ? 'entry has' : 'entries have'} not been added to a timesheet yet.`,
                14,
                25.5
            );
        }

        autoTable(doc, {
            head: [[
                'Date',
                'Last Name',
                'First Name',
                'Position',
                'Site Attended',
                'Originally Scheduled At',
                'In',
                'Out',
                'Hours',
                'Added By',
                'Added At',
                'Paycode',
                'Code',
            ]],
            body: rows.map((r: any) => [
                r?.Date ?? '',
                r?.LastName ?? '',
                r?.FirstName ?? '',
                r?.Position ?? '',
                r?.SiteWorked ?? '',
                r?.ScheduledSite ?? '',
                to12Hour(r?.ClockIn),
                to12Hour(r?.ClockOut),
                r?.Hours ?? '',
                r?.AddedBy ?? '',
                r?.AddedAt ?? '',
                r?.Paycode ?? '',
                r?.Code ?? '',
            ]),
            startY: uncoded > 0 ? 30 : 26,
            theme: 'grid',
            styles: { fontSize: 7, cellPadding: 1.5 },
            headStyles: { fontSize: 7, fontStyle: 'bold' },
            // An uncoded row is the one somebody has to do something about, so
            // it is the one that stands out on a printed page.
            didParseCell: (hookData: any) => {
                if (hookData.section === 'body' && rows[hookData.row.index]?.Coded === 'No') {
                    hookData.cell.styles.fontStyle = 'bold';
                }
            },
        });

        addPageNumber(doc);

        setPdfBlob(doc.output('blob'));
    };

    useEffect(() => {
        if (data) {
            generatePDF();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    return null;
};

export default UnscheduledAttendanceReportPdf;
